/*
 * Everything the browser remembers per device, behind one guarded accessor (REUSE J4). Two reasons
 * it is one place and not a call at each site: `localStorage` throws — when a browser refuses site
 * data and when the origin's shared quota is full — and a closed `Kind` union makes what teamBase
 * owns in this origin visible at a glance.
 *
 * Only per-device conveniences live here, and one tab-scoped guard. Drafts, the outbox and the
 * model are IndexedDB's (A19); the CSRF token and the session never touch storage at all
 * (architecture.md §6).
 */

/**
 * What is remembered. One entry per key shape, and the whole list of them (spec 12 §2.3).
 * `owner` is the account every other entry belongs to (core/session/device.ts); `protocolReload`
 * is the only one that lives in `sessionStorage`, because it must not outlive the tab;
 * `recentSearches` is the handful of terms the search page offers back on this device.
 */
export type Kind =
	| "theme"
	| "owner"
	| "paneWidths"
	| "lastRoute"
	| "announcerMuted"
	| "devFlags"
	| "shortcuts"
	| "recentSearches"
	| "protocolReload"
	/** How this person reads an AI chat's review: the layer, the diff's view and switches (aichat/review). */
	| "aichat"

/** The key one value is stored under: the namespace, the kind, then whatever narrows it. */
export function storageKey(kind: Kind, ...parts: string[]): string {
	return ["tb", kind, ...parts].join(":")
}

/** A value above this size is not stored; the quota is shared with the whole origin. */
export const MAX_VALUE_BYTES = 32 * 1024

// Encoded once: the cap is a byte cap, and a German UI string costs more bytes than it has
// characters, so `value.length` would let a value through at up to three times the size it claims.
const encoder = new TextEncoder()

function storage(): Storage | undefined {
	try {
		return globalThis.localStorage
	} catch {
		return undefined
	}
}

/** True while the browser lets this origin store anything at all. */
export function storageAvailable(): boolean {
	return storage() !== undefined
}

function sessionStore(): Storage | undefined {
	try {
		return globalThis.sessionStorage
	} catch {
		return undefined
	}
}

/**
 * The value of a key remembered for this tab only, or "". A tab-scoped value is what guards an
 * action that may run once per tab — the reload after close 4409 (protocol.md §6) — because a
 * device-scoped one would still be there in a new tab that has never seen the mismatch.
 */
export function readSessionValue(key: string): string {
	try {
		return sessionStore()?.getItem(key) ?? ""
	} catch {
		return ""
	}
}

/** Remembers one value for this tab; silent when the browser keeps nothing, like `writeValue`. */
export function writeSessionValue(key: string, value: string): void {
	try {
		const store = sessionStore()
		if (!store) return
		if (value === "") store.removeItem(key)
		else store.setItem(key, value)
	} catch {
		// A full or refused session store means the guard does not hold; that is the caller's risk.
	}
}

/** The stored value of one key, or "" — including when the browser refuses storage. */
export function readValue(key: string): string {
	try {
		return storage()?.getItem(key) ?? ""
	} catch {
		return ""
	}
}

/**
 * Stores one value, or removes the key when there is nothing left to keep. A value over the cap is
 * removed rather than truncated: half a value read back later is worse than none.
 */
export function writeValue(key: string, value: string): void {
	try {
		const store = storage()
		if (!store) return
		if (value === "" || encoder.encode(value).length > MAX_VALUE_BYTES) store.removeItem(key)
		else store.setItem(key, value)
	} catch {
		// The quota is shared with everything else on the origin; the value stays in memory.
	}
}

/** Reads a JSON value, or `undefined` when nothing valid is stored. */
export function readJSON<T>(key: string): T | undefined {
	const raw = readValue(key)
	if (raw === "") return undefined
	try {
		return JSON.parse(raw) as T
	} catch {
		return undefined
	}
}

/** Stores a JSON value under the same cap and the same silence on failure as writeValue. */
export function writeJSON(key: string, value: unknown): void {
	try {
		writeValue(key, JSON.stringify(value))
	} catch {
		// A value that cannot be serialised is a caller's bug, not a storage failure; drop it.
	}
}

/** Removes every value whose key starts with `prefix`, e.g. all pane widths of one layout. */
export function forgetValues(prefix: string): void {
	try {
		const store = storage()
		if (!store) return
		const stale: string[] = []
		for (let index = 0; index < store.length; index++) {
			const key = store.key(index)
			if (key?.startsWith(prefix)) stale.push(key)
		}
		for (const key of stale) store.removeItem(key)
	} catch {
		// Nothing to clean up in a browser that refuses storage.
	}
}

/** Removes everything teamBase owns in this origin — the sign-out sweep. */
export function forgetAll(): void {
	forgetValues("tb:")
	forgetSessionValues()
}

/** What this tab remembers: the protocol-reload guard, the last search term. */
function forgetSessionValues(): void {
	try {
		const store = sessionStore()
		if (!store) return
		const stale: string[] = []
		for (let index = 0; index < store.length; index++) {
			const key = store.key(index)
			if (key?.startsWith("tb")) stale.push(key)
		}
		for (const key of stale) store.removeItem(key)
	} catch {
		// A tab that refuses session storage has nothing to sweep.
	}
}
